"use client";

import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Ban, Loader2, Trash2, MoreHorizontal } from "lucide-react";
import { DeleteConfirmationDialog } from "@/components/shared/delete-dialog";
import { PermissionGate } from "@/components/shared/permission-gate";
import { toast } from "sonner";
import { del, patch } from "@/utils/api-handler";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { SCHEDULED_JOBS_API_PATH } from "@/components/scheduled-job/constants";
import {
    COMMAND_V2_ACTIVE_SCHEDULE_COUNT_QUERY_KEY,
    COMMAND_V2_SCHEDULES_QUERY_KEY,
} from "@/hooks/use-command-v2-schedules";


interface ScheduledJobsActionCellProps {
  row: { original: ScheduledJob };
}

export const ScheduledJobsActionCell: React.FC<ScheduledJobsActionCellProps> = ({
  row,
}) => {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const cancelMutation = useProtectedMutation({
    mutationFn: (jobId: string) => {
      return patch(`${SCHEDULED_JOBS_API_PATH}/${jobId}/cancel`, {});
    },
    requiredPermissions: [],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-jobs"] });
      queryClient.invalidateQueries({ queryKey: [COMMAND_V2_SCHEDULES_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [COMMAND_V2_ACTIVE_SCHEDULE_COUNT_QUERY_KEY],
      });
      toast.success("Scheduled job cancelled successfully");
    },
    onError: () => {
      toast.error("Failed to cancel scheduled job");
    },
  });

  const deleteMutation = useProtectedMutation({
    mutationFn: (jobId: string) => {
      return del(`${SCHEDULED_JOBS_API_PATH}/${jobId}`);
    },
    requiredPermissions: [],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-jobs"] });
      queryClient.invalidateQueries({ queryKey: [COMMAND_V2_SCHEDULES_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [COMMAND_V2_ACTIVE_SCHEDULE_COUNT_QUERY_KEY],
      });
      toast.success("Scheduled job deleted successfully", {
        description: "The job has been removed from the schedule.",
      });
    },
    onError: (error: any) => {
      toast.error("Failed to delete scheduled job", {
        description: error?.response?.data?.message,
      });
    },
  });

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(row.original.id || "");
    } catch (error) {
      console.error("Delete operation failed:", error);
    }
  };

  const handleCancel = () => {
    cancelMutation.mutate(row.original.id || "");
  };

  const itemName =
    row.original.description?.trim() ||
    row.original.job_name?.trim() ||
    (row.original.id ? `${row.original.id.slice(0, 8)}…` : "");

  return (
    <div className="flex items-center justify-end">
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
        itemName={itemName}
        itemType="scheduled job"
      />
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel scheduled job?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the scheduled job and prevent its next run.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelMutation.isPending}>
              Keep job
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={cancelMutation.isPending}
              className="bg-destructive text-destructive-foreground"
            >
              {cancelMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                <>
                  <Ban className="h-4 w-4" />
                  Cancel job
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="flex flex-col items-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="p-0"
              disabled={cancelMutation.isPending || deleteMutation.isPending}
            >
              <span className="sr-only">Open menu</span>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[150px] py-2 ">
            <PermissionGate requiredPermissions={[]}>
              <DropdownMenuItem
                className="cursor-pointer justify-start"
                onClick={() => setCancelDialogOpen(true)}
                disabled={cancelMutation.isPending || deleteMutation.isPending}
              >
                <Ban className="mr-2 h-4 w-4" />
                {cancelMutation.isPending ? "Cancelling..." : "Cancel"}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="justify-start text-destructive cursor-pointer hover:text-destructive"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleteMutation.isPending || cancelMutation.isPending}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {deleteMutation.isPending ? "Deleting..." : "Delete "}
              </DropdownMenuItem>
            </PermissionGate>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
